import React, { useState, useRef, useEffect } from 'react'
import { Vehicle } from '../types'
import { X, Minus, Square, Search, Settings } from 'lucide-react'

interface MacStyleVehiclePanelProps {
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  onVehicleSelect: (vehicle: Vehicle) => void
  onClose?: () => void
}

const MacStyleVehiclePanel: React.FC<MacStyleVehiclePanelProps> = ({
  vehicles,
  selectedVehicle,
  onVehicleSelect,
  onClose
}) => {
  const [position, setPosition] = useState({ x: 20, y: 80 })
  const [isDragging, setIsDragging] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const panelRef = useRef<HTMLDivElement>(null)

  // Filter vehicles based on search
  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.customerInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('drag-handle')) {
      setIsDragging(true)
      const rect = panelRef.current?.getBoundingClientRect()
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        })
      }
    }
  }

  // Handle drag move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset])

  // Get vehicle status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'moving': return 'bg-green-500'
      case 'stopped': return 'bg-red-500'
      case 'idle': return 'bg-yellow-500'
      case 'maintenance': return 'bg-purple-500'
      default: return 'bg-gray-500'
    }
  }

  // Get vehicle status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'moving': return 'En route'
      case 'stopped': return 'Arrêté'
      case 'idle': return 'Inactif'
      case 'maintenance': return 'Maintenance'
      default: return 'Hors ligne'
    }
  }

  if (isMinimized) {
    return (
      <div
        ref={panelRef}
        className="fixed z-50 bg-gray-800 rounded-lg shadow-2xl cursor-pointer"
        style={{ 
          left: position.x, 
          top: position.y,
          transform: isDragging ? 'scale(1.05)' : 'scale(1)',
          transition: isDragging ? 'none' : 'transform 0.2s ease'
        }}
        onClick={() => setIsMinimized(false)}
        onMouseDown={handleMouseDown}
      >
        <div className="px-4 py-2 text-white text-sm font-medium">
          🚗 Fleet ({vehicles.length})
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Content without title bar (handled by MacWindow) */}

      {/* Search bar */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search vehicles, customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Vehicle stats */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Total: {vehicles.length}</span>
          <span>Filtered: {filteredVehicles.length}</span>
          <div className="flex items-center space-x-3">
            <span className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
              {vehicles.filter(v => v.status === 'moving').length} Moving
            </span>
            <span className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
              {vehicles.filter(v => v.status === 'stopped').length} Stopped
            </span>
          </div>
        </div>
      </div>

      {/* Vehicle list */}
      <div className="flex-1 overflow-y-auto">
        {filteredVehicles.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <div className="text-lg mb-2">🔍</div>
              <div className="text-sm">No vehicles found</div>
            </div>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredVehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                onClick={() => onVehicleSelect(vehicle)}
                className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedVehicle?.id === vehicle.id
                    ? 'bg-blue-50 border-2 border-blue-200 shadow-sm'
                    : 'bg-white border border-gray-100 hover:bg-gray-50 hover:border-gray-200 hover:shadow-sm'
                }`}
              >
                {/* Vehicle header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(vehicle.status)}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{vehicle.name}</div>
                      <div className="text-sm text-gray-500 truncate">
                        {vehicle.identifierType || 'ID'}: {vehicle.plateNumber}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">{vehicle.speed} km/h</div>
                    <div className="text-xs text-gray-500">{getStatusText(vehicle.status)}</div>
                  </div>
                </div>

                {/* Customer/Container context */}
                <div className="space-y-1">
                  {vehicle.customerInfo?.name && (
                    <div className="text-xs text-blue-600 flex items-center">
                      <span className="mr-1">👤</span>
                      <span className="truncate">{vehicle.customerInfo.name}</span>
                    </div>
                  )}
                  {vehicle.containerInfo?.name && (
                    <div className="text-xs text-green-600 flex items-center">
                      <span className="mr-1">📦</span>
                      <span className="truncate">{vehicle.containerInfo.name}</span>
                    </div>
                  )}
                  {vehicle.containerInfo?.bookingReference && (
                    <div className="text-xs text-purple-600 flex items-center">
                      <span className="mr-1">📋</span>
                      <span className="truncate">Booking: {vehicle.containerInfo.bookingReference}</span>
                    </div>
                  )}
                  {vehicle.assetInfo?.code && (
                    <div className="text-xs text-orange-600 flex items-center">
                      <span className="mr-1">🚛</span>
                      <span className="truncate">{vehicle.assetInfo.type}: {vehicle.assetInfo.code}</span>
                    </div>
                  )}
                </div>

                {/* Vehicle details */}
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                      <span className="text-gray-500">Battery:</span>
                      <span className="ml-1 font-medium">{vehicle.batteryLevel || 'N/A'}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Signal:</span>
                      <span className="ml-1 font-medium">{vehicle.signalStrength || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Last update: {new Date(vehicle.lastUpdate).toLocaleDateString('en-GB')} {new Date(vehicle.lastUpdate).toLocaleTimeString('en-US', { 
                      hour12: true,
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with selection info */}
      {selectedVehicle && (
        <div className="px-4 py-2 bg-blue-50 border-t border-blue-100">
          <div className="text-xs text-blue-800">
            <div className="font-medium">Selected: {selectedVehicle.name}</div>
            <div className="text-blue-600">
              📍 {selectedVehicle.location.latitude.toFixed(4)}, {selectedVehicle.location.longitude.toFixed(4)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MacStyleVehiclePanel
